/*
 * Lipscore, loaded after the page instead of in the head. See
 * snippets/lipscore-loader.liquid and .docs/integrations/lipscore.md.
 *
 * Product cards render their rating widget inert: [data-lipscore-tile],
 * without the lipscore-rating-small class Lipscore scans for. Each is promoted
 * (given the class) only once its card nears the screen, so a widget in a
 * closed drawer, a hidden carousel tab or far down the grid is never requested.
 *
 * The script itself is only fetched when a widget is on the page. On a
 * product page that is as soon as the page has painted, for the rating, the
 * review list and the review data Lipscore adds for search engines.
 * Elsewhere it waits for the shopper's first interaction, or for the page to
 * load and go quiet (6 seconds at most for the load event), whichever comes
 * first. After that it is started once, then asked for each new batch of
 * promoted widgets. No polling.
 *
 * While the app embed is still on (it defines window.lipscoreInit before this
 * runs, or snippets/defer-third-party.liquid is holding its scripts until
 * load), it owns the script. This file then promotes widgets and asks
 * Lipscore to pick each batch up, which already keeps hidden and far-off
 * cards out of Lipscore's scan.
 */
(() => {
  if (window.RokaLipscore) return;

  const SCRIPT_SRC = "https://static.lipscore.com/assets/en/lipscore-v1.js";
  const TILE_SELECTOR = "[data-lipscore-tile]";
  const WIDGET_CLASS = "lipscore-rating-small";
  /* The product page's own widgets, which Lipscore finds by id. */
  const PAGE_WIDGET_SELECTOR = "#lipscore-rating, #lipscore-review-list";
  const NEAR_SCREEN_MARGIN = "200px";
  /* The same quiet-after-load timing and interactions as
     assets/wishlist-loader.js, which starts Wishlist King the same way. Two
     vendors, two files; change the timing in both. */
  const QUIET_AFTER_LOAD_MS = 3000;
  const IDLE_TIMEOUT_MS = 2000;
  /* A slow third-party file can hold the load event back for minutes on a
     poor connection, so the quiet wait starts by this point regardless. */
  const LOAD_WAIT_CAP_MS = 6000;
  /* No "scroll": a shopper's scroll always starts with one of these, and the
     scroll event also fires for the card's own programmatic peek, which runs
     while the page is still loading. */
  const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

  const readApiKey = () => {
    const config = document.getElementById("lipscore-loader-config");

    try {
      return String(JSON.parse(config?.textContent || "{}").apiKey || "");
    } catch {
      return "";
    }
  };

  /* snippets/defer-third-party.liquid turns the embed's scripts into inert
     text/plain ones until the page has loaded, so a held embed is still on. */
  const embedIsHeld = () =>
    [...document.querySelectorAll('script[data-roka-held="true"]')].some(
      (script) =>
        (script.dataset.deferredSrc || "").includes("static.lipscore.com") ||
        (script.textContent || "").includes("lipscoreInit"),
    );

  const embedOwnsScript =
    typeof window.lipscoreInit === "function" || embedIsHeld();
  const apiKey = readApiKey().trim();
  const themeOwnsScript = !embedOwnsScript && apiKey !== "";

  let scriptState = "idle";
  let triggered = false;
  let hasPromoted = false;
  let batchQueued = false;
  let quietTimer = 0;
  let loadCapTimer = 0;

  /* Lipscore starts itself only on the window load event, which a slow page
     can hold back indefinitely, so it is started here too. A second start
     is a no-op: Lipscore's init checks whether it has already begun. */
  const startLipscore = () => {
    try {
      window.lipscore?.initWidgets();
    } catch {
      /* Lipscore throws while its own init is mid-flight; its next pass
         covers the batch. */
    }
  };

  const loadScript = () => {
    if (!themeOwnsScript || scriptState !== "idle") return;

    scriptState = "loading";
    window.lipscoreInit = () => window.lipscore.init({ apiKey });

    const script = document.createElement("script");

    script.src = SCRIPT_SRC;
    script.async = true;
    /* Deferred already: snippets/defer-third-party.liquid lets it through
       rather than holding it and running a second copy on release. */
    script.dataset.rokaLoader = "lipscore";
    script.addEventListener("load", () => {
      scriptState = "ready";
      startLipscore();
    });
    document.head.append(script);
  };

  const hasWork = () =>
    hasPromoted || document.querySelector(PAGE_WIDGET_SELECTOR) !== null;

  /* A trigger with nothing to show loads nothing, but is remembered: a card
     that comes on screen later (a drawer opening) loads the script at once. */
  const trigger = () => {
    if (triggered) return;

    triggered = true;
    window.clearTimeout(quietTimer);
    window.clearTimeout(loadCapTimer);
    window.removeEventListener("load", waitForQuiet);

    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, trigger, { capture: true });
    }

    if (hasWork()) loadScript();
  };

  /* Runs on the load event or at the cap, whichever comes first. */
  function waitForQuiet() {
    if (quietTimer) return;

    window.clearTimeout(loadCapTimer);
    window.removeEventListener("load", waitForQuiet);

    quietTimer = window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(trigger, { timeout: IDLE_TIMEOUT_MS });
      } else {
        trigger();
      }
    }, QUIET_AFTER_LOAD_MS);
  }

  /* One initWidgets() per batch of cards that came on screen together. */
  const queueBatch = () => {
    if (batchQueued) return;

    batchQueued = true;
    window.requestAnimationFrame(() => {
      batchQueued = false;

      /* The embed's own loop only picks promoted widgets up while its "init
         repeat" setting is on, so they are handed over here as well. */
      if (embedOwnsScript) {
        if (window.lipscore) startLipscore();
        return;
      }

      if (scriptState === "ready") {
        startLipscore();
        return;
      }

      if (triggered) loadScript();
    });
  };

  const promote = (tile) => {
    tile.classList.add(WIDGET_CLASS);
    hasPromoted = true;
  };

  const observer =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            let promoted = false;

            for (const entry of entries) {
              if (!entry.isIntersecting) continue;

              observer.unobserve(entry.target);
              promote(entry.target);
              promoted = true;
            }

            if (promoted) queueBatch();
          },
          { rootMargin: NEAR_SCREEN_MARGIN },
        )
      : null;

  const watch = (tile) => {
    if (!tile || tile.classList.contains(WIDGET_CLASS)) return;

    if (observer) {
      observer.observe(tile);
      return;
    }

    promote(tile);
    queueBatch();
  };

  /* Filters and sorting replace the grid; a card that never came on screen
     would otherwise stay observed after it is gone. */
  const unwatch = (tile) => {
    if (tile) observer?.unobserve(tile);
  };

  window.RokaLipscore = { watch, unwatch };

  for (const tile of document.querySelectorAll(TILE_SELECTOR)) {
    watch(tile);
  }

  /* A colour change on the product page swaps in a fresh product section, and
     the theme editor re-renders sections: both bring rating and review
     elements Lipscore has not seen yet. */
  document.addEventListener("colourway:change", queueBatch);
  document.addEventListener("shopify:section:load", queueBatch);

  /* With the embed on, its loop takes the promoted widgets. With the embed
     off and no key in Theme settings → Lipscore, nothing can load them. */
  if (!themeOwnsScript) return;

  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, trigger, { capture: true, passive: true });
  }

  /* The product page's rating and review list, and the review data Lipscore
     adds for search engines, load once the page has painted rather than
     waiting for an interaction. */
  if (document.querySelector(PAGE_WIDGET_SELECTOR)) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(trigger, { timeout: IDLE_TIMEOUT_MS });
        } else {
          trigger();
        }
      });
    });
  }

  if (document.readyState === "complete") {
    waitForQuiet();
  } else {
    window.addEventListener("load", waitForQuiet, { once: true });
    loadCapTimer = window.setTimeout(waitForQuiet, LOAD_WAIT_CAP_MS);
  }
})();
